import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Search as SearchIcon, Sparkles, SlidersHorizontal } from 'lucide-react';
import { motion } from 'framer-motion';
import { publicApi } from '../utils/api';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const getCategoryLabel = (tool) =>
  tool.category || tool.primary_category || tool.category_name || tool.categories?.[0] || null;

const isFreeOrFreemium = (tool) => {
  const prices = (tool.pricing_tiers || []).map((tier) => String(tier.price || '').toLowerCase()).join(' ');
  const summary = String(tool.pricing_summary || tool.pricing || '').toLowerCase();
  const combined = `${prices} ${summary}`;
  return /free|freemium|\$0|0\s*\/\s*mo|0\s*per\s*month/.test(combined);
};

const SearchPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    pricing: 'all',
    rating: 'all',
  });

  const performSearch = useCallback(async (searchQuery, syncUrl = false) => {
    if (!searchQuery || searchQuery.length < 2) return;

    setLoading(true);
    setSearched(true);

    try {
      const response = await publicApi.search(searchQuery);
      setResults(response.data.results || []);
      if (syncUrl) {
        setSearchParams({ q: searchQuery });
      }
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [setSearchParams]);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams, performSearch]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        const response = await publicApi.search(trimmed);
        const raw = response.data?.results || [];
        const uniqueBySlug = new Map();
        raw.forEach((tool) => {
          if (!tool?.slug) return;
          uniqueBySlug.set(tool.slug, tool);
        });
        setSuggestions(Array.from(uniqueBySlug.values()).slice(0, 6));
      } catch {
        setSuggestions([]);
      }
    }, 240);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    results.forEach((tool) => {
      const label = getCategoryLabel(tool);
      if (label) set.add(label);
    });
    return ['all', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [results]);

  const filteredResults = useMemo(() => {
    return results.filter((tool) => {
      const category = getCategoryLabel(tool);
      const rating = tool?.rating?.overall;
      const free = isFreeOrFreemium(tool);

      if (filters.category !== 'all' && category !== filters.category) return false;
      if (filters.pricing === 'free' && !free) return false;
      if (filters.pricing === 'paid' && free) return false;
      if (filters.rating === '4' && !(rating >= 4)) return false;
      if (filters.rating === '3' && !(rating >= 3)) return false;
      return true;
    });
  }, [results, filters]);

  const contextualLinks = useMemo(() => {
    if (!filteredResults.length) return [];

    const categoryCounts = new Map();
    filteredResults.forEach((tool) => {
      const label = getCategoryLabel(tool);
      if (!label) return;
      categoryCounts.set(label, (categoryCounts.get(label) || 0) + 1);
    });

    const topCategory = [...categoryCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    const topToolSlugs = filteredResults.map((tool) => tool.slug).filter(Boolean).slice(0, 3);
    const links = [];

    if (topCategory) {
      links.push({
        to: `/categories/${slugify(topCategory)}`,
        label: `Browse more ${topCategory} tools`,
        description: `You have ${categoryCounts.get(topCategory)} ${topCategory} match${categoryCounts.get(topCategory) === 1 ? '' : 'es'}.`,
      });
    }

    if (topToolSlugs.length >= 2) {
      links.push({
        to: `/find?from=search&tools=${topToolSlugs.join(',')}`,
        label: 'Open Finder with top matches',
        description: 'Refine these results with stack and use-case recommendations.',
      });
    }

    links.push({
      to: '/consulting#packages',
      label: 'Get a shortlist from consulting',
      description: 'Need help deciding quickly? We can map the right stack for your workflow.',
    });

    return links;
  }, [filteredResults]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    performSearch(query.trim(), true);
  };

  const handleSuggestionClick = (name) => {
    setQuery(name);
    setShowSuggestions(false);
    performSearch(name, true);
  };

  return (
    <div className="min-h-screen bg-void pt-24" data-testid="search-page">
      <section
        className="relative overflow-visible"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(246,247,249,0.92), rgba(246,247,249,0.98)), url(${IMG.hero.search})`
            : `linear-gradient(rgba(8,8,18,0.75), rgba(8,8,18,0.96)), url(${IMG.hero.search})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 py-14 relative z-10">
          <Link 
            to="/" 
            className="inline-flex items-center text-text-secondary hover:text-cyber-yellow transition-colors mb-8"
            data-testid="back-link"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 
              className="text-4xl sm:text-5xl font-black text-white mb-8"
              style={{ fontFamily: 'Unbounded, sans-serif' }}
              data-testid="search-title"
            >
              Search AI Tools
            </h1>

            <form onSubmit={handleSubmit} className="mb-4">
              <div className="flex gap-4">
                <div className="flex-1 relative">
                  <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-muted" size={20} />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 120)}
                    placeholder="Search for AI tools..."
                    className="w-full pl-12 pr-4 py-4 backdrop-blur border focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow text-lg"
                    style={{
                      background: 'var(--card-premium-muted-bg)',
                      borderColor: 'var(--card-premium-border)',
                      color: 'var(--text-primary)',
                    }}
                    data-testid="search-input"
                  />

                  {showSuggestions && suggestions.length > 0 && (
                    <div
                      className="absolute z-40 mt-2 w-full rounded-xl border backdrop-blur p-2 shadow-2xl"
                      style={{
                        borderColor: 'var(--card-premium-border)',
                        background: 'var(--bg-glass-strong)',
                      }}
                    >
                      {suggestions.map((tool) => (
                        <button
                          key={tool.slug}
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleSuggestionClick(tool.name)}
                          className="w-full text-left px-3 py-2 rounded-lg transition-colors"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{tool.name}</p>
                          {tool.tagline && <p className="text-xs text-text-muted truncate">{tool.tagline}</p>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn-primary px-8"
                  data-testid="search-button"
                >
                  Search
                </button>
              </div>
            </form>

            <p className="text-xs text-text-muted mb-5">Try: content writing, sales outreach, meeting notes, customer support</p>

            {searched && (
              <div className="terminal-grid p-4 md:p-5">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-white">
                  <SlidersHorizontal size={15} className="text-cyber-yellow" />
                  Filter results
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="text-xs text-text-secondary">
                    Category
                    <select
                      value={filters.category}
                      onChange={(e) => setFilters((prev) => ({ ...prev, category: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border"
                      style={{ background: 'var(--card-premium-muted-bg)', borderColor: 'var(--card-premium-border)', color: 'var(--text-primary)' }}
                    >
                      {categoryOptions.map((option) => (
                        <option key={option} value={option}>{option === 'all' ? 'All categories' : option}</option>
                      ))}
                    </select>
                  </label>

                  <label className="text-xs text-text-secondary">
                    Pricing
                    <select
                      value={filters.pricing}
                      onChange={(e) => setFilters((prev) => ({ ...prev, pricing: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border"
                      style={{ background: 'var(--card-premium-muted-bg)', borderColor: 'var(--card-premium-border)', color: 'var(--text-primary)' }}
                    >
                      <option value="all">All pricing</option>
                      <option value="free">Free / Freemium</option>
                      <option value="paid">Paid only</option>
                    </select>
                  </label>

                  <label className="text-xs text-text-secondary">
                    Minimum rating
                    <select
                      value={filters.rating}
                      onChange={(e) => setFilters((prev) => ({ ...prev, rating: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border"
                      style={{ background: 'var(--card-premium-muted-bg)', borderColor: 'var(--card-premium-border)', color: 'var(--text-primary)' }}
                    >
                      <option value="all">Any rating</option>
                      <option value="4">4.0+</option>
                      <option value="3">3.0+</option>
                    </select>
                  </label>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Results */}
      <section className="container mx-auto px-6 pb-24">
        {loading && (
          <div className="text-center text-text-secondary">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow"></div>
            <p className="mt-4">Searching...</p>
          </div>
        )}

        {!loading && searched && filteredResults.length === 0 && (
          <div className="text-center text-text-secondary py-12">
            <p>No tools found for "{query}"</p>
          </div>
        )}

        {!loading && filteredResults.length > 0 && (
          <div>
            <p className="text-text-secondary mb-6">
              Found {filteredResults.length} result{filteredResults.length !== 1 ? 's' : ''} for "{query}"
            </p>

            {contextualLinks.length > 0 && (
              <div className="terminal-grid p-5 mb-6">
                <div className="inline-flex items-center gap-2 text-sm font-semibold text-white mb-3">
                  <Sparkles size={15} className="text-cyber-yellow" />
                  Next best actions
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {contextualLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="premium-tool-muted rounded-xl p-3 border transition-colors"
                    >
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{link.label}</p>
                      <p className="text-xs mt-1 text-text-muted">{link.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-results">
              {filteredResults.map((tool, index) => (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="terminal-grid premium-tool-card p-6 card-hover"
                  data-testid={`result-${tool.slug}`}
                >
                  {(tool.logo_url || tool.cover_image) && (
                    <img 
                      src={tool.logo_url || tool.cover_image}
                      alt={tool.name}
                      className="w-16 h-16 object-contain mb-4"
                    />
                  )}
                  
                  <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                    {tool.name}
                  </h3>
                  
                  {tool.tagline && (
                    <p className="text-sm text-text-secondary mb-2">
                      {tool.tagline}
                    </p>
                  )}

                  {tool.description && (
                    <p className="text-xs mb-4 line-clamp-3" style={{ color: 'var(--text-muted)' }}>
                      {String(tool.description).replace(/\n+/g, ' ')}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2 mb-4">
                    {getCategoryLabel(tool) && (
                      <span className="text-[11px] px-2 py-1 rounded-full border text-text-secondary" style={{ borderColor: 'var(--card-premium-border)' }}>
                        {getCategoryLabel(tool)}
                      </span>
                    )}
                    {tool.pricing_tiers?.[0]?.price && (
                      <span className="text-[11px] px-2 py-1 rounded-full border border-cyber-yellow/35 text-cyber-yellow">
                        {tool.pricing_tiers[0].price}
                      </span>
                    )}
                    {tool.rating?.overall && (
                      <span className="text-[11px] px-2 py-1 rounded-full border border-laser-green/35 text-laser-green">
                        {Number(tool.rating.overall).toFixed(1)} rating
                      </span>
                    )}
                  </div>
                  
                  <Link
                    to={`/tools/${tool.slug}`}
                    className="premium-tool-cta inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold"
                  >
                    View Details
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SearchPage;
